import Box from '@mui/material/Box';
import { Table } from '@openx/design-system/components/Table/Table';
import { TableCell } from '@openx/design-system/components/Table/TableCell';
import { TablePagination } from '@openx/design-system/components/Table/TablePagination';
import { TableRow } from '@openx/design-system/components/Table/TableRow';
import { type ReactNode, type SyntheticEvent, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { OpenXAgentTableColumn } from './utils/types';
import { useKeepInViewport } from './utils/useKeepInViewport';
import type { OpenXAgentTablePagination } from './utils/usePagination';

type OpenXAgentTableProps = {
  ariaLabel: string;
  dataTest: string;
  columns: OpenXAgentTableColumn[];
  minWidth: number;
  pagination?: OpenXAgentTablePagination;
  children: ReactNode;
};

export const OpenXAgentTable = ({
  ariaLabel,
  dataTest,
  columns,
  minWidth,
  pagination,
  children,
}: OpenXAgentTableProps) => {
  const { t } = useTranslation();
  const footerRef = useRef<HTMLElement>(null);
  const keepFooterInViewport = useKeepInViewport(footerRef, pagination?.page);

  const handlePageChange = useCallback(
    (_event: SyntheticEvent | null, zeroBasedPage: number) => {
      keepFooterInViewport();
      pagination?.onPageChange(zeroBasedPage + 1);
    },
    [keepFooterInViewport, pagination]
  );

  return (
    <>
      <Box sx={{ mt: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
        <Table aria-label={ariaLabel} data-test={dataTest} sx={{ minWidth, tableLayout: 'fixed' }}>
          <colgroup>
            {columns.map(({ id, width }) => (
              <Box component="col" key={id} sx={{ width }} />
            ))}
          </colgroup>

          {children}
        </Table>
      </Box>

      {pagination && pagination.pageCount > 1 ? (
        <Box component="nav" ref={footerRef} aria-label={t('Table pagination')}>
          <TablePagination
            count={pagination.count}
            page={pagination.page - 1}
            rowsPerPage={pagination.pageSize}
            rowsPerPageOptions={[]}
            onPageChange={handlePageChange}
            labelDisplayedRows={({ from, to, count }) => t('{from}–{to} of {total}', { from, to, total: count })}
            getItemAriaLabel={direction => (direction === 'previous' ? t('Previous page') : t('Next page'))}
          />
        </Box>
      ) : null}
    </>
  );
};

export const OpenXAgentTableEmptyRow = ({ colSpan, children }: { colSpan: number; children: ReactNode }) => (
  <TableRow>
    <TableCell colSpan={colSpan} align="center" sx={{ color: 'text.secondary' }}>
      {children}
    </TableCell>
  </TableRow>
);
